import React, { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Divider } from '@mui/material';

import { LatestProcessCriteriaDTOTalentStatus } from '@/api/generated';
import ChipSelect from '@/components/fields/ChipSelect';
import DateRange from '@/components/fields/DateRange/DateRange';
import DropdownSelect from '@/components/fields/DropdownSelect';
import Field from '@/components/fields/Field';
import TalentStatusSelect from '@/components/fields/TalentStatusSelect';
import TextInput from '@/components/fields/TextInput';
import OfficeDropdownSelect from '@/views/fields/OfficeDropdownSelect/OfficeDropdownSelect';
import OrganizationDropdownSelect from '@/views/fields/OrganizationDropdownSelect/OrganizationDropdownSelect';
import ClientUserDropdownSelect from '@/views/fields/UserDropdownSelect/ClientUserDropdownSelect';
import CreateByDropdownSelect from '@/views/fields/UserDropdownSelect/CreatedByDropdownSelect';
import { TalentSectionHiddenFields } from '@/views/Filters/constants';
import { getHiringDecisions, getTalentLimited, getTalentScores, getTalentTypes } from '@/views/Filters/mocks/options';

import DropdownSection from './DropdownSection';

const TALENT_PROCESS_STATUSES = [
  LatestProcessCriteriaDTOTalentStatus.REGISTERED,
  LatestProcessCriteriaDTOTalentStatus.IN_TEST,
  LatestProcessCriteriaDTOTalentStatus.COMPLETED,
  LatestProcessCriteriaDTOTalentStatus.GHOST
];

interface TalentSectionProps {
  name: string;
  hiddenFields?: Set<TalentSectionHiddenFields>;
}

const TalentSection: FC<TalentSectionProps> = props => {
  const { t } = useTranslation('filters');
  const { name, hiddenFields = new Set() } = props;

  const isStatusShown = !hiddenFields.has(TalentSectionHiddenFields.STATUS);
  const isEmailShown = !hiddenFields.has(TalentSectionHiddenFields.EMAIL);
  const isPhoneShown = !hiddenFields.has(TalentSectionHiddenFields.PHONE);
  const isLimitedTalentShown = !hiddenFields.has(TalentSectionHiddenFields.LIMITED_TALENT);
  const isOrganizationShown = !hiddenFields.has(TalentSectionHiddenFields.ORGANIZATION);
  const isOfficeShown = !hiddenFields.has(TalentSectionHiddenFields.OFFICE);
  const isKamShown = !hiddenFields.has(TalentSectionHiddenFields.KAM);
  const isOverallScoreShown = !hiddenFields.has(TalentSectionHiddenFields.OVERALL_SCORE);
  const isHiringDecisionShown = !hiddenFields.has(TalentSectionHiddenFields.HIRING_DECISION);

  return (
    <DropdownSection title={t('filters.sections.talent.title')} {...props}>
      <Field
        component={TextInput}
        name={`${name}.searchByName`}
        label={t('filters.sections.talent.name')}
        withFormControl
      />
      <Divider />
      <Field
        component={ChipSelect}
        name={`${name}.type`}
        label={t('filters.sections.talent.type')}
        innerProps={{
          options: getTalentTypes(t),
          sx: { mx: -0.5 }
        }}
        withFormControl
      />
      <Divider />
      {isEmailShown && (
        <>
          <Field
            component={TextInput}
            name={`${name}.searchByEmail`}
            label={t('filters.sections.talent.email')}
            withFormControl
          />
          <Divider />
        </>
      )}
      {isPhoneShown && (
        <>
          <Field
            component={TextInput}
            name={`${name}.talentPhone`}
            label={t('filters.sections.talent.phone')}
            withFormControl
          />
          <Divider />
        </>
      )}
      {isStatusShown && (
        <>
          <Field
            component={TalentStatusSelect}
            name={`${name}.latestProcess.talentStatus`}
            label={t('filters.sections.talent.status')}
            withFormControl
            innerProps={{
              activeOptions: TALENT_PROCESS_STATUSES
            }}
          />
          <Divider />
        </>
      )}
      {isOverallScoreShown && (
        <>
          <Field
            component={DropdownSelect}
            name={`${name}.overallScore`}
            label={t('filters.sections.talent.overallScore')}
            innerProps={{
              options: getTalentScores(t)
            }}
            withFormControl
          />
          <Divider />
        </>
      )}
      {isHiringDecisionShown && (
        <>
          <Field
            component={DropdownSelect}
            name={`${name}.hiringDecision`}
            label={t('filters.sections.talent.hiringDecision')}
            innerProps={{
              options: getHiringDecisions(t)
            }}
            withFormControl
          />
          <Divider />
        </>
      )}
      {isOfficeShown && (
        <>
          <Field
            component={OfficeDropdownSelect}
            name={`${name}.office`}
            label={t('filters.sections.talent.office')}
            innerProps={{ isMulti: false }}
            withFormControl
          />
          <Divider />
        </>
      )}
      {isOrganizationShown && (
        <>
          <Field
            component={OrganizationDropdownSelect}
            name={`${name}.organization`}
            label={t('filters.sections.talent.organization')}
            innerProps={{ isMulti: false }}
            withFormControl
          />
          <Divider />
        </>
      )}
      {isKamShown && (
        <>
          <Field
            component={ClientUserDropdownSelect}
            name={`${name}.kam`}
            label={t('filters.sections.talent.kam')}
            withFormControl
          />
          <Divider />
        </>
      )}
      {isLimitedTalentShown && (
        <>
          <Field
            component={ChipSelect}
            name={`${name}.limitedTalent`}
            label={t('filters.sections.talent.limitedTalent')}
            innerProps={{
              options: getTalentLimited(t)
            }}
            withFormControl
          />
          <Divider />
        </>
      )}
      <Field
        component={CreateByDropdownSelect}
        name={`${name}.createdBy`}
        label={t('filters.sections.talent.createdBy')}
        withFormControl
      />
      <Divider />
      <Field
        component={DateRange}
        name={`${name}.createdAt`}
        label={t('filters.sections.talent.creationDate')}
        withFormControl
      />
      <Divider />
      <Field
        component={DateRange}
        name={`${name}.lastActivity`}
        label={t('filters.sections.talent.updateDate')}
        withFormControl
      />
    </DropdownSection>
  );
};

export default TalentSection;
